import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isEqual from 'lodash/isEqual';

import { DataGrid } from '@p81-common/ui';

import {
  AdditionalModal,
  ButtonIcon,
  EmptyState,
  FilterButton,
  Loading,
  PageTitle,
  SaveFilterBlock
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';
import ModalFeature from './parts/ModalFeature/ModalFeature';

import {
  createMainTableConf,
  defaultFilters,
  defaultNavigation
} from './helpers/configurates';
import { getRequest, putRequest, deleteRequest } from 'utils/requests';
import { showNotif } from 'utils/notification';
import {
  replaceHistory,
  checkingForExistenceOfOptions,
  clearOrDefaultFilters,
  getOriginalTableSettingsListItems
} from 'utils/common';
import { createReqBody } from './helpers/helpers';
import { access, hasAccess, resources } from 'utils/permissions';
import { defaultNumOfRowsOptions } from 'utils/settings.js';

import classes from './FeatureList.module.scss';

const FeatureList = () => {
  //   filters and pagination
  const [filter, setFilter] = useState({
    ...defaultFilters,
    ...defaultNavigation
  });

  const [filterBackend, setFilterBackend] = useState({
    key: 'featureflag',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState<boolean | string>(
    false
  );
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);

  const [filterButton, setFilterButton] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [tableSettingsListItems, setTableSettingsListItems] = useState<any[]>(
    []
  );

  //data from api
  const [dataForTable, setDataForTable] = useState<{
    data?: any[];
    totalRecords?: number;
    totalPages?: number;
    page?: number;
  }>({});
  const [filters, setFilters] = useState({});

  //other data
  const [loader, setLoader] = useState(true);
  const [loaderTable, setLoaderTable] = useState(false);
  const [dataConection, setDataConection] = useState(false);
  const [selectLoader, setSelectLoader] = useState(false);

  // if you touch this trigger, then a request for filter lists is sent
  const [startFilter, setStartFilter] = useState(false);
  // status for comparison whether new options have been selected
  const [prevStateFilter, setPrevStateFilter] = useState<any>();

  const [triggerStartEffect, setTriggerStartEffect] = useState(false);

  const [isOpenAdditional, setIsOpenAdditional] = useState(false);
  const [infoForAdditionalModal, setInfoForAdditionalModal] = useState({});

  const { t } = useTranslation(['featureFlags']);

  const history = useHistory();
  const query = new URLSearchParams(history.location.search);

  const unmount = useRef(false);
  const hasFeatureFlagWriteAccess = hasAccess([
    resources.PRODUCT_FEATURE_FLAG,
    access.WRITE
  ]);

  const [limitRows, setLimitRows] = useState(defaultNumOfRowsOptions);

  const onOpenAdditionalModal = (info: any) => {
    setInfoForAdditionalModal(info);
    setIsOpenAdditional(true);
  };

  const handleFilterInput = (e: any) => {
    setFilter({ ...filter, search: e.target.value, currentPage: 1 });
    setStartFilter(!startFilter);
  };

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, (value: string) =>
    handleFilterInput({ target: { value } })
  );

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const onChangeNumOfRows = (limit: number) => {
    return function () {
      setVisibleFilterSave(true);
      if (filter.itemsPerPage === limit) {
        return;
      }
      setFilter({ ...filter, itemsPerPage: limit, currentPage: 1 });
      setLimitRows(
        limitRows.map(rowSizeOption => ({
          ...rowSizeOption,
          selected: rowSizeOption.name === limit
        }))
      );
      setStartFilter(!startFilter);
    };
  };

  const handleTableSettingsMenuItemClick = (value: any) => {
    return function () {
      setColumnVisibility(prev => ({
        ...prev,
        [value]: prev[value] === false ? true : false
      }));

      // Also update the tableSettingsListItems
      setTableSettingsListItems(prev =>
        prev.map(item =>
          item.name === value ? { ...item, selected: !item.selected } : item
        )
      );
    };
  };

  const onSortEnd = (args: { oldIndex: number; newIndex: number }) => {
    const { oldIndex, newIndex } = args;
    if (oldIndex !== newIndex) {
      const reorderedList = [...tableSettingsListItems];
      const [movedItem] = reorderedList.splice(oldIndex, 1);
      reorderedList.splice(newIndex, 0, movedItem);
      setTableSettingsListItems(reorderedList);
    }
  };

  const handleSortable = (field: any) => {
    if (field === filter.fieldName) {
      const directionTemp = filter.direction * -1;
      setFilter({ ...filter, direction: directionTemp });
      setStartFilter(!startFilter);
    } else {
      setFilter({ ...filter, fieldName: field });
      setStartFilter(!startFilter);
    }
  };

  const changeCurrentPage = (e: any) => {
    setFilter({ ...filter, currentPage: e });
    setStartFilter(!startFilter);
  };

  // a function to check if the saved filters have a given combination of
  // the selected options and if so, switch to the saved filter

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const getFilterOptionsFromBackend = (listFilter: any) => {
    setSelectLoader(true);
    getRequest('/product/fflag/list/filters')
      .then(res => {
        if (!unmount.current) {
          const { body } = res;
          setFilters({ ...filters, ...body });
          // we are checking or in the new filter lists there are
          // parameters that are selected, and those that are not, turn off the selection
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );
          newCheckingList && setPrevStateFilter(newCheckingList);
          newCheckingList &&
            replaceHistory(
              filter,
              undefined,
              history,
              'validation',
              newCheckingList,
              changeFilter
            );
          setSelectLoader(false);
        }
      })
      .catch(err => {
        !unmount.current && console.log(err);
      });
  };

  useEffect(() => {
    //after the select is closed, we get into this function.
    //We check or after closing the filter whether there have
    //been changes in the selected options and if so, we make a request
    if (triggerStartEffect) {
      if (
        !isEqual(prevStateFilter, filter) &&
        JSON.parse(query.get('filter') || 'null') !== null
      ) {
        replaceHistory(filter, undefined, history, false, false, changeFilter);
      }
    }
    setTriggerStartEffect(true);
    // eslint-disable-next-line
  }, [startFilter]);

  useEffect(() => {
    // if (enabledRequest) { // were need for mode validation to clear some value in select
    setLoaderTable(true);
    if (
      query.get('filter') === null &&
      isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      })
    ) {
      replaceHistory(filter, undefined, history, 'replace', false, 'Default');
      setVisibleFilterEdit(false);
      setVisibleFilterSave(false);
      setPrevStateFilter(filter);
      requestForFilteredData();
    } else if (
      query.get('filter') === null &&
      !isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      })
    ) {
      resetAllfilters(true);
      replaceHistory(
        filter,
        // setEnabledRequest,
        undefined,
        history,
        'replace',
        {
          ...defaultFilters,
          ...defaultNavigation
        },
        'Default'
      );
      setVisibleFilterEdit(false);
      setVisibleFilterSave(false);
    } else {
      const filterParam = query.get('filter') || '{}';
      const changeFilterParam = query.get('changeFilter') || 'Default';

      setFilter(JSON.parse(filterParam));
      setPrevStateFilter(JSON.parse(filterParam));
      requestForFilteredData(JSON.parse(filterParam));
      setChangeFilter(changeFilterParam);
    }
    setTrigerResetDatePicker(!trigerResetDatePicker);
    // }
    // setEnabledRequest(true);

    // eslint-disable-next-line
  }, [query.get('filter') || query.get('jarvisRegion')]);

  const requestForFilteredData = (listFilter = filter) => {
    const linkForRequest = `/product/fflag/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;
    const REQ_BODY = createReqBody(listFilter);

    putRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!unmount.current) {
          setDataForTable(res.body);
          setDataConection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter);
        }
      })
      .catch(err => {
        if (!unmount.current) {
          setDataConection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };

  const handleDeleteFeature = async (featureId: string) => {
    try {
      await deleteRequest('/product/fflag/delete', { featureId });

      showNotif({
        message: t('featureFlags:DELETE_SUCCESS'),
        kind: 'success',
        dismissAfter: 5000,
        type: 'alert'
      });

      // Refresh the list after successful deletion
      requestForFilteredData();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || t('featureFlags:DELETE_ERROR');
      showNotif({
        message: errorMessage,
        kind: 'error',
        dismissAfter: 5000,
        type: 'alert'
      });
      throw err; // Re-throw to let the component handle loading states
    }
  };

  const resetAllfilters = (
    reload?: any,
    onlyFilterLabel?: any,
    onClickDefault?: any,
    modeClearAll?: any
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
    } else {
      setVisibleFilterSave(true);
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
    }

    setTrigerResetDatePicker(!trigerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  // Create table configuration with all the handlers (after functions are defined)
  const originalTableConf = createMainTableConf({
    onOpenAdditionalModal,
    setIsOpenAdditional,
    requestForFilteredData,
    onDeleteFeature: handleDeleteFeature
  });

  // Initialize tableSettingsListItems if not yet initialized
  useEffect(() => {
    if (tableSettingsListItems.length === 0 && originalTableConf.length > 0) {
      const initialSettings =
        getOriginalTableSettingsListItems(originalTableConf);
      setTableSettingsListItems(initialSettings);
    }
  }, [originalTableConf, tableSettingsListItems.length]);

  // Build the column configuration based on the current order and visibility
  const dataGridTableConf = useMemo(() => {
    if (tableSettingsListItems.length === 0) {
      // Return default config if not yet initialized
      return originalTableConf.map(col => ({
        ...col,
        show: columnVisibility[col.headerName] !== false
      }));
    }

    // Build ordered config from tableSettingsListItems
    const middleColumns = tableSettingsListItems
      .map(item => {
        const col = originalTableConf.find(c => c.headerName === item.name);
        if (col) {
          return {
            ...col,
            show: item.selected && columnVisibility[col.headerName] !== false
          };
        }
        return null;
      })
      .filter((col): col is NonNullable<typeof col> => col !== null);

    const orderedConfig = [
      originalTableConf[0], // Always keep first column
      ...middleColumns,
      originalTableConf[originalTableConf.length - 1] // Always keep last column (actions)
    ];

    return orderedConfig;
  }, [originalTableConf, tableSettingsListItems, columnVisibility]);

  if (loader) {
    return <Loading />;
  }

  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      {isOpenAdditional && (
        <AdditionalModal
          isOpen={isOpenAdditional}
          setIsOpen={setIsOpenAdditional}
          info={infoForAdditionalModal}
        />
      )}
      <PageTitle title={t('featureFlags:FEATURE_LIST')} />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('featureFlags:SEARCH_PLACEHOLDER')}
          value={filter.search}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={trigerResetDatePicker}
          setTrigerResetDatePicker={setTrigerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
        />
      </div>
      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={trigerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      {hasFeatureFlagWriteAccess && (
        <div className={classes.actions}>
          <ButtonIcon
            icon="close"
            buttonText={t('activity:buttons.ADD_NEW_FEATURE')}
            rotateIcon={45}
            onClick={() =>
              onOpenAdditionalModal({
                content: (
                  <ModalFeature
                    setIsOpenAdditional={setIsOpenAdditional}
                    requestForFilteredData={requestForFilteredData}
                  />
                )
              })
            }
            marginLeftAuto={true}
            className={classes.btnAddFeature}
          />
        </div>
      )}
      <div className={classes.tableWrap}>
        <DataGrid
          autoHeight={true}
          classes={{ container: classes.wrapDG }}
          tableConfig={dataGridTableConf.filter(item => item.show) as any}
          tableData={{
            data: dataForTable.data || [],
            totalRecords: dataForTable.totalRecords || 0,
            page: dataForTable.page || 1,
            totalPages: dataForTable.totalPages || 1
          }}
          numOfRowsOptions={limitRows}
          initialState={{
            pinnedColumns: {
              left: [dataGridTableConf[0]?.field],
              right: ['actions']
            }
          }}
          paginationProps={{
            page: filter.currentPage - 1,
            count: dataForTable.totalRecords || 0,
            rowsPerPage:
              limitRows.find(item => item.selected === true)?.name ||
              filter.itemsPerPage,
            onChangePage: (_: any, page: number) => {
              changeCurrentPage(page + 1);
            }
          }}
          toolbarProps={{
            title1: t('featureFlags:FEATURE_RESULTS'),
            title2: `(${dataForTable.totalRecords || 0})`,
            tableSettingsTooltipText: 'Table Settings',
            rowSelectionTooltipText: t('activity:buttons.QUANTITY_OF_ITEMS'),
            tableSettingsListItems: tableSettingsListItems,
            onChangeNumOfRows: onChangeNumOfRows as any,
            handleTableSettingsMenuItemClick,
            onSortEnd
          }}
          onSortModelChange={(a: any) => {
            handleSortable(a[0]?.field);
          }}
          sortingMode={'server'}
          paginationMode={'server'}
          loading={loaderTable}
          rowCount={dataForTable.totalRecords || 0}
          getRowId={(row: any) => row._id}
        />
      </div>
    </>
  );
};

export default FeatureList;
